import React, { useEffect, useState } from "react";
import { Pause, Play, Trash2, Users, FileText, UserCog, X, LogIn, Save, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AdminAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", owner_email: "", appsumo_tier: "Tier1" });
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/admin/agencies");
    setAgencies(data);
  };

  useEffect(() => { load(); }, []);

  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, owner_email: a.owner_email, appsumo_tier: a.appsumo_tier || "Tier1" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/agencies/${editing.id}`, form);
      toast.success("Agency updated");
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const approveUpgrade = async (a) => {
    if (!confirm(`Approve upgrade to ${a.requested_tier}?`)) return;
    await api.post(`/admin/agencies/${a.id}/set-tier`, { tier: a.requested_tier });
    toast.success(`${a.name} upgraded to ${a.requested_tier}`);
    await load();
  };

  const approveCancel = async (a) => {
    if (!confirm(`Confirm cancellation for "${a.name}"? Account will be suspended.`)) return;
    await api.post(`/admin/agencies/${a.id}/approve-cancel`);
    toast.success("Subscription cancelled");
    await load();
  };

  const impersonate = async (a) => {
    if (!confirm(`Log in as "${a.name}"? Your admin session will be replaced until you sign out.`)) return;
    try {
      await api.post(`/admin/agencies/${a.id}/impersonate`);
      toast.success(`Now viewing as ${a.name}`);
      navigate("/agency");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSuspend = async (a) => {
    await api.patch(`/admin/agencies/${a.id}/suspend`, { suspend: !a.is_suspended });
    await load();
  };

  const remove = async (a) => {
    if (!confirm(`Permanently delete "${a.name}" and all its data?`)) return;
    await api.delete(`/admin/agencies/${a.id}`);
    await load();
  };

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Tenants</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-8" data-testid="admin-agencies-title">Agencies</h1>

      <div className="bento overflow-hidden" data-testid="admin-agencies-table">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">Agency</th>
              <th className="px-6 py-4">Owner</th>
              <th className="px-6 py-4">Tier</th>
              <th className="px-6 py-4">Subscription</th>
              <th className="px-6 py-4">Clients</th>
              <th className="px-6 py-4">Signed</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {agencies.length === 0 && (
              <tr><td colSpan="7" className="px-6 py-16 text-center text-slate-500">No agencies yet.</td></tr>
            )}
            {agencies.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`admin-agency-row-${a.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: a.branding?.primary_color || "#0F172A" }}>
                      {a.name?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{a.subdomain}</p>
                    </div>
                    {a.is_suspended && <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">suspended</span>}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600 font-mono">{a.owner_email}</td>
                <td className="px-6 py-4"><span className="text-xs bg-slate-100 px-2 py-1 rounded-full font-mono">{a.appsumo_tier || "Tier1"}</span></td>
                <td className="px-6 py-4">
                  {a.subscription_status === "cancel_requested" && (
                    <button onClick={() => approveCancel(a)} className="text-xs bg-red-50 text-red-700 hover:bg-red-100 px-2 py-1 rounded-full inline-flex items-center gap-1" data-testid={`approve-cancel-${a.id}`}>
                      Cancel requested · approve
                    </button>
                  )}
                  {a.subscription_status === "upgrade_requested" && (
                    <button onClick={() => approveUpgrade(a)} className="text-xs bg-violet-50 text-violet-700 hover:bg-violet-100 px-2 py-1 rounded-full inline-flex items-center gap-1" data-testid={`approve-upgrade-${a.id}`}>
                      Upgrade to {a.requested_tier} · approve
                    </button>
                  )}
                  {a.subscription_status === "active" && <span className="text-xs text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />active</span>}
                  {a.subscription_status === "cancelled" && <span className="text-xs text-slate-400">cancelled</span>}
                </td>
                <td className="px-6 py-4 text-slate-700"><span className="inline-flex items-center gap-1 text-sm"><Users className="w-3.5 h-3.5 text-slate-400" />{a.client_count || 0}</span></td>
                <td className="px-6 py-4 text-slate-700"><span className="inline-flex items-center gap-1 text-sm"><FileText className="w-3.5 h-3.5 text-slate-400" />{a.contracts_signed || 0}</span></td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <button onClick={() => impersonate(a)} title="Log in as this agency" className="inline-flex items-center gap-1 text-sm text-white bg-slate-900 hover:bg-slate-700 px-3 py-1.5 rounded-lg" data-testid={`impersonate-${a.id}`}>
                    <LogIn className="w-3.5 h-3.5" /> Enter
                  </button>
                  <button onClick={() => openEdit(a)} title="Edit details" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-lg hover:bg-slate-100 ml-1" data-testid={`edit-agency-${a.id}`}>
                    <UserCog className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => toggleSuspend(a)} title={a.is_suspended ? "Unsuspend" : "Suspend"} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-lg hover:bg-slate-100 ml-1" data-testid={`suspend-${a.id}`}>
                    {a.is_suspended ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => remove(a)} className="inline-flex items-center gap-1 text-sm text-red-500 hover:bg-red-50 px-2 py-1.5 rounded-lg ml-1" data-testid={`delete-agency-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form onSubmit={saveEdit} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-agency-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="eyebrow mb-1">Edit agency</p>
                <h2 className="text-2xl font-bold tracking-tight">{editing.name}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-4 h-4" /></button>
            </div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Agency name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4" data-testid="edit-name-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Owner email</label>
            <input required type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 font-mono text-sm" data-testid="edit-email-input" />
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Tier</label>
            <div className="grid grid-cols-4 gap-2 mb-6">
              {["Tier1", "Tier2", "Tier3", "Tier4"].map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, appsumo_tier: t })}
                  className={`py-2.5 rounded-xl text-sm font-medium ${form.appsumo_tier === t ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`edit-tier-${t}`}>
                  {t}
                </button>
              ))}
            </div>
            <button type="submit" className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95 inline-flex items-center justify-center gap-2" data-testid="save-agency-button">
              <Save className="w-4 h-4" /> Save changes
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
